import { useFeatureValue } from "@growthbook/growthbook-react";
import * as Sentry from "@sentry/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  findMatches,
  Redaction,
  type RedactionMarkers,
  type RedactionNotice,
} from "./redaction";
import { usePatientDataInputRuleSeen } from "../gpt/stores/patientDataInputRuleStore";

// The Markers of the current UI language: Redaction writes them, the bubble styles them.
export function useRedactionMarkers(): RedactionMarkers {
  const { t } = useTranslation();
  return {
    name_with_birth_date: t("[name and date of birth]"),
    patient_name: t("[name]"),
    contact: t("[contact details]"),
    hospital_identifier: t("[hospital identifier]"),
    address: t("[address]"),
    national_identifier: t("[national identifier]"),
  };
}

// The route is data: the surface resolves it at delivery, so a retry reads the state of its own render.
type GatedSend<Route> =
  | { kind: "redacted"; question: string; route: Route }
  | {
      kind: "handedOver";
      question: string;
      notice: RedactionNotice | null;
      route: Route;
    };

// Where the text of a send that the detector failed on waits for a retry (OD-04).
type FailedTextPlace =
  // GPTNewChatPage: the composer keeps the text, so the next send replaces the failure.
  | { kind: "composer" }
  // A conversation: each failed text stays as an unsent message after the Questions sent before it.
  | { kind: "transcript"; questionCount: number };

// Why a send stayed on the device: the device detector failed, or the server
// could not check the text (AD-20). Each one says so in the unsent message.
export type UnsentReason = "check_failed" | "check_unavailable";

type Unsent<Route> = {
  id: string;
  gated: GatedSend<Route>;
  questionCount: number;
  reason: UnsentReason;
};

// The only way a React surface reaches Redaction: every assistant send goes through `sendRedacted`.
export function useQuestionRedaction<Route>(input: {
  // False on a surface of another engine: every question passes unchanged (AD-13).
  enabled: boolean;
  // Sends a checked Question on its route.
  deliver: (redaction: Redaction, route: Route) => void;
  failedTextPlace: FailedTextPlace;
}) {
  // A kill switch: an unknown flag (boot, CDN outage) keeps Redaction on.
  const flag = useFeatureValue("WEBAPP_PATIENT_DATA_REDACTION", true);
  const enabled = input.enabled && flag;
  const [inputRuleSeen, markInputRuleSeen] = usePatientDataInputRuleSeen();
  const markers = useRedactionMarkers();
  // The sends the detector failed on, oldest first. Their raw text stays in memory on the device (OD-04).
  const [unsent, setUnsent] = useState<ReadonlyArray<Unsent<Route>>>([]);
  // The latest unsent message, until a Question follows it. A transcript count cannot tell: a regenerate drops answered cards.
  const [retriableId, setRetriableId] = useState<string | null>(null);
  // The last send that left the device, with its place: the server can refuse it after the send.
  const lastDelivered = useRef<{
    gated: GatedSend<Route>;
    questionCount: number;
  } | null>(null);
  const place = input.failedTextPlace;
  // The composer has no transcript.
  const questionCount = place.kind === "transcript" ? place.questionCount : 0;

  function redact(gated: GatedSend<Route>): Redaction {
    if (!enabled) return Redaction.unchecked(gated.question);
    switch (gated.kind) {
      case "redacted":
        return Redaction.run(gated.question, markers);
      case "handedOver":
        return Redaction.handedOver(gated.question, gated.notice, markers);
      default:
        throw new Error(gated satisfies never);
    }
  }

  // A failing detector sends nothing, and the surface shows the error with the text.
  function gate(gated: GatedSend<Route>, retriedId: string | null): void {
    let redaction: Redaction;
    try {
      redaction = redact(gated);
    } catch (error) {
      Sentry.captureException(error);
      // A failed retry keeps its message in place.
      if (retriedId !== null) return;
      const failed = {
        id: crypto.randomUUID(),
        gated,
        questionCount,
        reason: "check_failed" as const,
      };
      setRetriableId(failed.id);
      switch (place.kind) {
        case "composer":
          setUnsent([failed]);
          return;
        case "transcript":
          setUnsent((previous) => [...previous, failed]);
          return;
        default:
          throw new Error(place satisfies never);
      }
    }
    switch (place.kind) {
      case "composer":
        setUnsent([]);
        break;
      // An unsent message leaves the transcript only through its own retry.
      case "transcript":
        if (retriedId !== null)
          setUnsent((previous) =>
            previous.filter((entry) => entry.id !== retriedId)
          );
        break;
      default:
        throw new Error(place satisfies never);
    }
    // Every Question passes here, so this one follows each unsent message.
    setRetriableId(null);
    lastDelivered.current = { gated, questionCount };
    input.deliver(redaction, gated.route);
  }

  return {
    // The rule promises a removal that a disabled Redaction does not make.
    showInputRule: enabled && !inputRuleSeen,
    dismissInputRule: markInputRuleSeen,
    // Only the latest one retries, and only while no Question follows it.
    // The retry replays the failed send's route under the current flag: it keeps the origin and a handed-over Notice.
    unsent: unsent.map((entry) => ({
      id: entry.id,
      question: entry.gated.question,
      questionCount: entry.questionCount,
      reason: entry.reason,
      // The text goes back to the composer: the unsent message leaves.
      edit: () => {
        setUnsent((previous) =>
          previous.filter((other) => other.id !== entry.id)
        );
        if (entry.id === retriableId) setRetriableId(null);
      },
      retry:
        entry.id === retriableId
          ? () => {
              gate(entry.gated, entry.id);
            }
          : null,
    })),
    // The landing's start-over empties the composer, so its failure goes too. A transcript keeps each unsent message.
    discardUnsent: (): void => {
      if (place.kind === "transcript")
        throw new Error(
          "should not happen: a transcript discards an unsent message"
        );
      setUnsent([]);
      setRetriableId(null);
    },
    sendRedacted: (question: string, route: Route): void => {
      gate({ kind: "redacted", question, route }, null);
    },
    // The server refused the last send: it stored nothing, and the text stays on the device to edit or retry (AD-20).
    // The send is captured now, and shown by the returned call: a newer send can follow before it shows.
    captureRefusal: (): (() => void) => {
      const delivered = lastDelivered.current;
      if (delivered === null)
        throw new Error("should not happen: a refusal before any send");
      const failed = {
        id: crypto.randomUUID(),
        gated: delivered.gated,
        questionCount: delivered.questionCount,
        reason: "check_unavailable" as const,
      };
      return () => {
        // A newer send owns the Retry slot: the refused message stays editable.
        if (lastDelivered.current === delivered) setRetriableId(failed.id);
        switch (place.kind) {
          case "composer":
            setUnsent([failed]);
            return;
          case "transcript":
            setUnsent((previous) => [...previous, failed]);
            return;
          default:
            throw new Error(place satisfies never);
        }
      };
    },
    // A Question that another screen redacted, with that screen's Notice.
    sendHandedOver: (
      question: string,
      notice: RedactionNotice | null,
      route: Route
    ): void => {
      gate({ kind: "handedOver", question, notice, route }, null);
    },
    // A local display of what `sendRedacted` sends. Null on a failing detector, which `sendRedacted` reports.
    preview: (question: string): string | null => {
      if (!enabled) return question;
      try {
        return Redaction.run(question, markers).text;
      } catch {
        return null;
      }
    },
    // A failing detector blocks the lookup too, since the token may sit inside a Match.
    overlapsMatch: enabled
      ? (text: string, start: number, end: number): boolean => {
          try {
            return findMatches(text).some(
              (m) => m.start < end && start < m.end
            );
          } catch (error) {
            Sentry.captureException(error);
            return true;
          }
        }
      : null,
  };
}
